import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { Button, InputNumber, Tooltip, Slider, Input, Popconfirm } from 'antd';
import {
    EditOutlined, DragOutlined, DeleteOutlined,
    PlusOutlined, UndoOutlined, ColumnHeightOutlined,
    BorderOutlined, ScissorOutlined, FontSizeOutlined,
} from '@ant-design/icons';
import { unicodeLabel, buildRoundedPath, normalizeAccentPath, insertAccentPath } from './glyphEditorState';

const TOOLS = { PEN: 'pen', SELECT: 'select' };
const POINT_RADIUS = 6;
const HANDLE_RADIUS = 5;
const CLOSE_THRESHOLD = 12; // pixels - how close to first point to auto-close

/**
 * GlyphBezierEditor — Illustrator/Figma-style pen tool with:
 *  - Click to place corner points
 *  - Click+drag to place smooth points with symmetric bezier handles
 *  - Click near first point to close the path
 *  - Select tool to drag any point or handle
 *  - Double-click a segment to insert a point
 */
const GlyphBezierEditor = ({ glyph, metadata, onMetadataChange, onGlyphChange, onBack, accents = [], onAccentsChange, allGlyphs = [], onSwitchGlyph }) => {
    const canvasRef = useRef(null);
    const [tool, setTool] = useState(TOOLS.PEN);
    const [dragIdx, setDragIdx] = useState(null);
    const [dragField, setDragField] = useState(null);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [advanceWidth, setAdvanceWidth] = useState(glyph.advanceWidth || 600);
    const [showMetrics, setShowMetrics] = useState(true);

    // Accent state
    const [accentNaming, setAccentNaming] = useState(false);
    const [accentName, setAccentName] = useState('');
    const [showAccents, setShowAccents] = useState(true);
    const [selectedIndices, setSelectedIndices] = useState(new Set()); // multi-select for accents
    // Pen state for click-drag curve creation
    const [penDragging, setPenDragging] = useState(false);
    const [penDragStart, setPenDragStart] = useState(null);
    // Preview: show line from last point to cursor
    const [cursorPos, setCursorPos] = useState(null);
    // Grid
    const [showGrid, setShowGrid] = useState(false);
    const [gridSize, setGridSize] = useState(50);
    // Symmetry line
    const [showSymmetry, setShowSymmetry] = useState(false);
    // Snap to grid
    const [snapToGrid, setSnapToGrid] = useState(false);
    // Mirror mode (live symmetric drawing)
    const [mirrorMode, setMirrorMode] = useState(false);
    // Pan & Zoom
    const [zoom, setZoom] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState(null);
    const [spaceHeld, setSpaceHeld] = useState(false);
    // Context strip (sibling glyphs)
    const [showContext, setShowContext] = useState(true);
    const contextStripRef = useRef(null);

    const upm = metadata.unitsPerEm || 1000;
    const ascender = metadata.ascender || 800;
    const descender = metadata.descender || -200;
    const xHeight = metadata.xHeight ?? Math.round(ascender * 0.65);
    const capHeight = metadata.capHeight ?? Math.round(ascender * 0.9);

    // --- Coordinate transforms (with zoom & pan) ---
    const getTransform = useCallback((canvas) => {
        const rect = canvas.getBoundingClientRect();
        const padding = 60;
        const drawH = rect.height - padding * 2;
        const baseScale = drawH / (ascender - descender);
        const scale = baseScale * zoom;
        const offsetX = (padding + 40) * zoom + panX;
        const offsetY = (padding + ascender * baseScale) * zoom + panY;
        return { scale, offsetX, offsetY, rect };
    }, [ascender, descender, zoom, panX, panY]);

    const toCanvas = useCallback((x, y, t) => ({
        cx: t.offsetX + x * t.scale,
        cy: t.offsetY - y * t.scale,
    }), []);

    const snapValue = useCallback((val) => {
        if (!snapToGrid || gridSize <= 0) return val;
        return Math.round(val / gridSize) * gridSize;
    }, [snapToGrid, gridSize]);

    const fromCanvas = useCallback((cx, cy, t) => ({
        x: snapValue(Math.round((cx - t.offsetX) / t.scale)),
        y: snapValue(Math.round((t.offsetY - cy) / t.scale)),
    }), [snapValue]);

    // --- Mirror helper: reflect x across the symmetry axis ---
    const mirrorX = useCallback((x) => advanceWidth - x, [advanceWidth]);

    const mirrorCmd = useCallback((cmd) => {
        if (cmd.type === 'Z') return { type: 'Z' };
        const m = { ...cmd };
        if (m.x != null) m.x = mirrorX(m.x);
        if (m.x1 != null) m.x1 = mirrorX(m.x1);
        if (m.x2 != null) m.x2 = mirrorX(m.x2);
        return m;
    }, [mirrorX]);

    // Find the mirror-paired point index for a given index
    const findMirrorIdx = useCallback((idx, path) => {
        if (!mirrorMode || !path || idx == null) return null;
        const cmd = path[idx];
        if (!cmd || cmd.x == null) return null;
        const mx = mirrorX(cmd.x);
        const my = cmd.y;
        const threshold = 8;
        let best = null, bestDist = Infinity;
        for (let i = 0; i < path.length; i++) {
            if (i === idx) continue;
            const c = path[i];
            if (c.x == null) continue;
            const dist = Math.abs(c.x - mx) + Math.abs(c.y - my);
            if (dist < threshold && dist < bestDist) { best = i; bestDist = dist; }
        }
        return best;
    }, [mirrorMode, mirrorX]);

    // --- Find first moveTo point of the CURRENT (last) contour for close detection ---
    const getFirstPoint = useCallback(() => {
        const path = glyph.path || [];
        // Walk backwards to find the last M command — that's the start
        // of the current (most recent) contour being drawn.
        for (let i = path.length - 1; i >= 0; i--) {
            if (path[i].type === 'M') return { x: path[i].x, y: path[i].y };
        }
        return null;
    }, [glyph]);

    // --- Get prev/next on-curve point indices within the SAME contour ---
    const getContourNeighbors = useCallback((idx) => {
        const path = glyph.path || [];
        if (idx < 0 || idx >= path.length) return { prev: null, next: null, isClosed: false };

        // Find contour boundaries for this index
        let contourStart = 0;
        for (let i = idx; i >= 0; i--) {
            if (path[i].type === 'M') { contourStart = i; break; }
        }
        let contourEnd = path.length - 1;
        for (let i = idx + 1; i < path.length; i++) {
            if (path[i].type === 'M') { contourEnd = i - 1; break; }
        }
        const isClosed = path[contourEnd]?.type === 'Z';

        // Collect on-curve point indices within this contour only
        const pointIndices = [];
        for (let i = contourStart; i <= contourEnd; i++) {
            if (path[i].type !== 'Z' && path[i].x != null) pointIndices.push(i);
        }

        const piIdx = pointIndices.indexOf(idx);
        if (piIdx === -1) return { prev: null, next: null, isClosed };

        const prevPtIdx = piIdx > 0 ? pointIndices[piIdx - 1] : (isClosed ? pointIndices[pointIndices.length - 1] : null);
        const nextPtIdx = piIdx < pointIndices.length - 1 ? pointIndices[piIdx + 1] : (isClosed ? pointIndices[0] : null);

        return {
            prev: prevPtIdx != null ? path[prevPtIdx] : null,
            next: nextPtIdx != null ? path[nextPtIdx] : null,
            isClosed
        };
    }, [glyph]);

    // --- Draw ---
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, rect.width, rect.height);
        const t = getTransform(canvas);

        // Guide lines
        const guides = [
            { y: 0, label: `Baseline (0)`, color: '#8b949e', dash: [] },
            { y: ascender, label: `Ascender (${ascender})`, color: '#1f6feb', dash: [6, 3] },
            { y: descender, label: `Descender (${descender})`, color: '#f85149', dash: [6, 3] },
            { y: xHeight, label: `x-Height (${xHeight})`, color: '#3fb950', dash: [4, 4] },
            { y: capHeight, label: `Cap Height (${capHeight})`, color: '#d29922', dash: [4, 4] },
        ];
        for (const g of guides) {
            const { cy } = toCanvas(0, g.y, t);
            ctx.strokeStyle = g.color + '88';
            ctx.lineWidth = 1;
            ctx.setLineDash(g.dash);
            ctx.beginPath();
            ctx.moveTo(0, cy);
            ctx.lineTo(rect.width, cy);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = g.color;
            ctx.font = '10px monospace';
            ctx.fillText(g.label, 4, cy - 5);
        }

        // Advance width
        const { cx: awX } = toCanvas(advanceWidth, 0, t);
        ctx.strokeStyle = '#da3633aa';
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(awX, 0);
        ctx.lineTo(awX, rect.height);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#da3633';
        ctx.font = '10px monospace';
        ctx.fillText(`Width (${advanceWidth})`, awX + 4, 14);

        // Origin
        const { cx: originX } = toCanvas(0, 0, t);
        ctx.strokeStyle = '#da363344';
        ctx.beginPath();
        ctx.moveTo(originX, 0);
        ctx.lineTo(originX, rect.height);
        ctx.stroke();

        // --- Grid ---
        if (showGrid && gridSize > 0) {
            ctx.strokeStyle = '#ffffff0d';
            ctx.lineWidth = 0.5;
            // Vertical lines from 0 to advanceWidth
            for (let gx = 0; gx <= advanceWidth; gx += gridSize) {
                const { cx } = toCanvas(gx, 0, t);
                ctx.beginPath();
                ctx.moveTo(cx, toCanvas(0, ascender, t).cy);
                ctx.lineTo(cx, toCanvas(0, descender, t).cy);
                ctx.stroke();
            }
            // Horizontal lines from descender to ascender
            for (let gy = descender; gy <= ascender; gy += gridSize) {
                const { cy } = toCanvas(0, gy, t);
                const x0 = toCanvas(0, 0, t).cx;
                const x1 = toCanvas(advanceWidth, 0, t).cx;
                ctx.beginPath();
                ctx.moveTo(x0, cy);
                ctx.lineTo(x1, cy);
                ctx.stroke();
            }
        }

        // --- Symmetry line ---
        if (showSymmetry) {
            const midX = advanceWidth / 2;
            const { cx: symCx } = toCanvas(midX, 0, t);
            const topY = toCanvas(0, ascender, t).cy;
            const botY = toCanvas(0, descender, t).cy;
            ctx.strokeStyle = '#a371f7';
            ctx.lineWidth = 1;
            ctx.setLineDash([6, 4]);
            ctx.beginPath();
            ctx.moveTo(symCx, topY);
            ctx.lineTo(symCx, botY);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = '#a371f7';
            ctx.font = '10px monospace';
            ctx.fillText(`Sym (${Math.round(midX)})`, symCx + 4, topY + 14);
        }

        // --- Draw glyph outline (use rounded path for display) ---
        const path = glyph.path || [];
        const displayPath = buildRoundedPath(path);
        if (displayPath.length > 0) {
            ctx.beginPath();
            for (const cmd of displayPath) {
                const p = toCanvas(cmd.x || 0, cmd.y || 0, t);
                switch (cmd.type) {
                    case 'M': ctx.moveTo(p.cx, p.cy); break;
                    case 'L': ctx.lineTo(p.cx, p.cy); break;
                    case 'C': {
                        const c1 = toCanvas(cmd.x1, cmd.y1, t);
                        const c2 = toCanvas(cmd.x2, cmd.y2, t);
                        ctx.bezierCurveTo(c1.cx, c1.cy, c2.cx, c2.cy, p.cx, p.cy);
                        break;
                    }
                    case 'Q': {
                        const c1 = toCanvas(cmd.x1, cmd.y1, t);
                        ctx.quadraticCurveTo(c1.cx, c1.cy, p.cx, p.cy);
                        break;
                    }
                    case 'Z': ctx.closePath(); break;
                }
            }
            ctx.strokeStyle = '#58a6ff';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.fillStyle = '#58a6ff15';
            ctx.fill('evenodd');
        }

        // --- Mirror preview (ghost) ---
        if (mirrorMode && path.length > 0) {
            const mirroredPath = path.map(cmd => mirrorCmd(cmd));
            const mirrorDisplayPath = buildRoundedPath(mirroredPath);
            ctx.beginPath();
            for (const cmd of mirrorDisplayPath) {
                const p = toCanvas(cmd.x || 0, cmd.y || 0, t);
                switch (cmd.type) {
                    case 'M': ctx.moveTo(p.cx, p.cy); break;
                    case 'L': ctx.lineTo(p.cx, p.cy); break;
                    case 'C': {
                        const c1 = toCanvas(cmd.x1, cmd.y1, t);
                        const c2 = toCanvas(cmd.x2, cmd.y2, t);
                        ctx.bezierCurveTo(c1.cx, c1.cy, c2.cx, c2.cy, p.cx, p.cy);
                        break;
                    }
                    case 'Q': {
                        const c1 = toCanvas(cmd.x1, cmd.y1, t);
                        ctx.quadraticCurveTo(c1.cx, c1.cy, p.cx, p.cy);
                        break;
                    }
                    case 'Z': ctx.closePath(); break;
                }
            }
            ctx.strokeStyle = '#a371f788';
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = '#a371f70a';
            ctx.fill('evenodd');

            // Mirror control points (ghost)
            for (let i = 0; i < path.length; i++) {
                const cmd = path[i];
                if (cmd.type === 'Z') continue;
                const mc = mirrorCmd(cmd);
                if (mc.x != null && mc.y != null) {
                    const p = toCanvas(mc.x, mc.y, t);
                    ctx.fillStyle = '#a371f744';
                    ctx.fillRect(p.cx - 4, p.cy - 4, 8, 8);
                    ctx.strokeStyle = '#a371f788';
                    ctx.lineWidth = 1;
                    ctx.strokeRect(p.cx - 4, p.cy - 4, 8, 8);
                }
            }
        }

        // --- Preview line from last point to cursor (pen tool) ---
        if (tool === TOOLS.PEN && cursorPos && path.length > 0) {
            const lastCmd = [...path].reverse().find(c => c.x != null);
            const isClosed = path[path.length - 1]?.type === 'Z';
            if (lastCmd && !isClosed) {
                const lp = toCanvas(lastCmd.x, lastCmd.y, t);
                ctx.strokeStyle = '#58a6ff55';
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.moveTo(lp.cx, lp.cy);
                ctx.lineTo(cursorPos.cx, cursorPos.cy);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }

        // --- Draw handles and points ---
        for (let i = 0; i < path.length; i++) {
            const cmd = path[i];
            if (cmd.type === 'Z') continue;

            // Bezier handle lines + dots (only show for selected node)
            if (cmd.type === 'C') {
                const c2 = toCanvas(cmd.x2, cmd.y2, t);
                const p = toCanvas(cmd.x, cmd.y, t);

                // Handle2 (x2,y2): incoming/left handle of the CURRENT point
                // Show when current point is selected
                if (selectedIdx === i) {
                    ctx.strokeStyle = '#58a6ff66';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(p.cx, p.cy);
                    ctx.lineTo(c2.cx, c2.cy);
                    ctx.stroke();
                    ctx.fillStyle = '#3fb950';
                    ctx.strokeStyle = '#fff';
                    ctx.lineWidth = 1.5;
                    ctx.beginPath();
                    ctx.arc(c2.cx, c2.cy, HANDLE_RADIUS, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.stroke();
                }
            }
            if (cmd.type === 'Q') {
                const c1 = toCanvas(cmd.x1, cmd.y1, t);
                ctx.fillStyle = '#3fb950';
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(c1.cx, c1.cy, HANDLE_RADIUS, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
            }

            // On-curve points (squares)
            if (cmd.x != null && cmd.y != null) {
                const p = toCanvas(cmd.x, cmd.y, t);
                const isSelected = selectedIdx === i;
                const isMultiSelected = selectedIndices.has(i);
                const isFirst = i === 0;
                const size = isFirst ? POINT_RADIUS + 1 : POINT_RADIUS;
                ctx.fillStyle = isSelected ? '#f0883e' : isMultiSelected ? '#da8ee7' : (isFirst ? '#e6edf3' : '#58a6ff');
                ctx.fillRect(p.cx - size, p.cy - size, size * 2, size * 2);
                ctx.strokeStyle = isSelected ? '#fff' : isMultiSelected ? '#da8ee7' : (isFirst ? '#58a6ff' : '#fff');
                ctx.lineWidth = isFirst ? 2 : isMultiSelected ? 2 : 1;
                ctx.strokeRect(p.cx - size, p.cy - size, size * 2, size * 2);

                // Corner radius indicator & draggable handle
                if (cmd.cornerRadius > 0) {
                    // Small badge for non-selected points
                    if (!isSelected) {
                        ctx.fillStyle = '#d29922';
                        ctx.beginPath();
                        ctx.arc(p.cx + size + 4, p.cy - size - 4, 3.5, 0, Math.PI * 2);
                        ctx.fill();
                    }

                    // Draggable diamond handle on the angle bisector (selected only)
                    if (isSelected) {
                        // Find prev & next on-curve points within same contour
                        const { prev, next } = getContourNeighbors(i);
                        if (prev && next) {
                            // Compute bisector direction
                            const dxIn = prev.x - cmd.x, dyIn = prev.y - cmd.y;
                            const dxOut = next.x - cmd.x, dyOut = next.y - cmd.y;
                            const lenIn = Math.hypot(dxIn, dyIn);
                            const lenOut = Math.hypot(dxOut, dyOut);
                            if (lenIn > 0 && lenOut > 0) {
                                const nxIn = dxIn / lenIn, nyIn = dyIn / lenIn;
                                const nxOut = dxOut / lenOut, nyOut = dyOut / lenOut;
                                // Bisector = normalized sum of the two unit vectors
                                let bx = nxIn + nxOut, by = nyIn + nyOut;
                                const bLen = Math.hypot(bx, by);
                                if (bLen > 0.01) {
                                    bx /= bLen; by /= bLen;
                                } else {
                                    // Segments are collinear, use perpendicular
                                    bx = -nyIn; by = nxIn;
                                }
                                const handleDist = cmd.cornerRadius; // handle at actual radius distance
                                const hx = cmd.x + bx * handleDist;
                                const hy = cmd.y + by * handleDist;
                                const hp = toCanvas(hx, hy, t);

                                // Dashed arc showing effective (clamped) radius
                                const maxR = Math.min(lenIn, lenOut) * 0.5;
                                const effectiveR = Math.min(cmd.cornerRadius, maxR);

                                // Dashed arc showing radius extent
                                ctx.strokeStyle = '#d2992244';
                                ctx.lineWidth = 1;
                                ctx.setLineDash([4, 4]);
                                ctx.beginPath();
                                ctx.arc(p.cx, p.cy, effectiveR * t.scale, 0, Math.PI * 2);
                                ctx.stroke();
                                ctx.setLineDash([]);

                                // Line from corner to handle
                                ctx.strokeStyle = '#d2992266';
                                ctx.lineWidth = 1;
                                ctx.beginPath();
                                ctx.moveTo(p.cx, p.cy);
                                ctx.lineTo(hp.cx, hp.cy);
                                ctx.stroke();

                                // Diamond handle
                                const hs = 5;
                                ctx.fillStyle = '#d29922';
                                ctx.strokeStyle = '#fff';
                                ctx.lineWidth = 1.5;
                                ctx.beginPath();
                                ctx.moveTo(hp.cx, hp.cy - hs);
                                ctx.lineTo(hp.cx + hs, hp.cy);
                                ctx.lineTo(hp.cx, hp.cy + hs);
                                ctx.lineTo(hp.cx - hs, hp.cy);
                                ctx.closePath();
                                ctx.fill();
                                ctx.stroke();
                            }
                        }
                    }
                }
            }
        }

        // --- Draw outgoing handle (x1,y1 of next C) for the selected point ---
        if (selectedIdx != null) {
            const selPt = path[selectedIdx];
            if (selPt && selPt.x != null) {
                // Find the next C command after the selected point
                for (let i = selectedIdx + 1; i < path.length; i++) {
                    if (path[i].type === 'Z') break;
                    if (path[i].type === 'C') {
                        const nextC = path[i];
                        const sp = toCanvas(selPt.x, selPt.y, t);
                        const h1 = toCanvas(nextC.x1, nextC.y1, t);
                        // Line from selected point to outgoing handle
                        ctx.strokeStyle = '#58a6ff66';
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(sp.cx, sp.cy);
                        ctx.lineTo(h1.cx, h1.cy);
                        ctx.stroke();
                        // Handle circle
                        ctx.fillStyle = '#3fb950';
                        ctx.strokeStyle = '#fff';
                        ctx.lineWidth = 1.5;
                        ctx.beginPath();
                        ctx.arc(h1.cx, h1.cy, HANDLE_RADIUS, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.stroke();
                        break;
                    }
                    if (path[i].x != null) break; // next point is not a curve
                }
            }
        }

        // Highlight first point if pen tool and cursor is near it (close indicator)
        // Only show when the current (last) contour has more than 2 points
        let currentContourPts = 0;
        for (let i = path.length - 1; i >= 0; i--) {
            if (path[i].type === 'Z') break;
            if (path[i].x != null) currentContourPts++;
        }
        if (tool === TOOLS.PEN && cursorPos && currentContourPts > 2) {
            const first = getFirstPoint();
            if (first) {
                const fp = toCanvas(first.x, first.y, t);
                const dist = Math.hypot(cursorPos.cx - fp.cx, cursorPos.cy - fp.cy);
                if (dist < CLOSE_THRESHOLD) {
                    ctx.strokeStyle = '#3fb950';
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.arc(fp.cx, fp.cy, CLOSE_THRESHOLD, 0, Math.PI * 2);
                    ctx.stroke();
                }
            }
        }
    }, [glyph, ascender, descender, xHeight, capHeight, advanceWidth, selectedIdx, selectedIndices, toCanvas, getTransform, tool, cursorPos, getFirstPoint, getContourNeighbors, showGrid, gridSize, showSymmetry, mirrorMode, mirrorCmd]);

    useEffect(() => { draw(); }, [draw]);
    useEffect(() => {
        const h = () => draw();
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [draw]);

    // --- Hit testing ---
    const hitTest = useCallback((mx, my) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const t = getTransform(canvas);
        const path = glyph.path || [];
        const threshold = 10;

        // First check corner radius handles (higher priority when visible)
        if (selectedIdx != null) {
            const cmd = path[selectedIdx];
            if (cmd && cmd.cornerRadius > 0 && (cmd.type === 'L' || cmd.type === 'M')) {
                const { prev, next } = getContourNeighbors(selectedIdx);
                if (prev && next) {
                    const dxIn = prev.x - cmd.x, dyIn = prev.y - cmd.y;
                    const dxOut = next.x - cmd.x, dyOut = next.y - cmd.y;
                    const lenIn = Math.hypot(dxIn, dyIn), lenOut = Math.hypot(dxOut, dyOut);
                    if (lenIn > 0 && lenOut > 0) {
                        const nxIn = dxIn / lenIn, nyIn = dyIn / lenIn;
                        const nxOut = dxOut / lenOut, nyOut = dyOut / lenOut;
                        let bx = nxIn + nxOut, by = nyIn + nyOut;
                        const bLen = Math.hypot(bx, by);
                        if (bLen > 0.01) { bx /= bLen; by /= bLen; }
                        else { bx = -nyIn; by = nxIn; }
                        const handleDist = cmd.cornerRadius;
                        const hx = cmd.x + bx * handleDist;
                        const hy = cmd.y + by * handleDist;
                        const hp = toCanvas(hx, hy, t);
                        if (Math.hypot(mx - hp.cx, my - hp.cy) < threshold) {
                            return { idx: selectedIdx, field: 'cornerRadius' };
                        }
                    }
                }
            }
        }

        for (let i = 0; i < path.length; i++) {
            const cmd = path[i];
            if (cmd.type === 'Z') continue;
            if (cmd.type === 'C') {
                const c1 = toCanvas(cmd.x1, cmd.y1, t);
                if (Math.hypot(mx - c1.cx, my - c1.cy) < threshold) return { idx: i, field: 'handle1' };
                const c2 = toCanvas(cmd.x2, cmd.y2, t);
                if (Math.hypot(mx - c2.cx, my - c2.cy) < threshold) return { idx: i, field: 'handle2' };
            }
            if (cmd.type === 'Q') {
                const c1 = toCanvas(cmd.x1, cmd.y1, t);
                if (Math.hypot(mx - c1.cx, my - c1.cy) < threshold) return { idx: i, field: 'handle1' };
            }
            if (cmd.x != null) {
                const p = toCanvas(cmd.x, cmd.y, t);
                if (Math.hypot(mx - p.cx, my - p.cy) < threshold) return { idx: i, field: 'point' };
            }
        }
        return null;
    }, [glyph, getTransform, toCanvas, selectedIdx, getContourNeighbors]);

    // ===== PEN TOOL: mousedown =====
    const handlePenDown = useCallback((mx, my, canvas, e) => {
        const t = getTransform(canvas);
        const { x, y } = fromCanvas(mx, my, t);
        const path = glyph.path || [];
        const isClosed = path.length > 0 && path[path.length - 1]?.type === 'Z';


        // If path is closed, start a new contour
        if (isClosed) {
            const newPath = [...path, { type: 'M', x, y }];
            onGlyphChange({ ...glyph, path: newPath });
            setPenDragging(true);
            setPenDragStart({ x, y, idx: newPath.length - 1 });
            return;
        }

        // Check if clicking near the first point of the current contour → close the path
        // Count points in the current (last) contour only — not the entire path
        let currentContourPoints = 0;
        for (let i = path.length - 1; i >= 0; i--) {
            if (path[i].type === 'Z') break;
            if (path[i].x != null) currentContourPoints++;
        }
        if (currentContourPoints > 2) {
            const first = getFirstPoint();
            if (first) {
                const fp = toCanvas(first.x, first.y, t);
                if (Math.hypot(mx - fp.cx, my - fp.cy) < CLOSE_THRESHOLD) {
                    onGlyphChange({ ...glyph, path: [...path, { type: 'Z' }] });
                    return;
                }
            }
        }

        // Check if clicking an existing point → select it for dragging
        const hit = hitTest(mx, my);
        if (hit) {
            setDragIdx(hit.idx);
            setDragField(hit.field);
            setSelectedIdx(hit.idx);
            // Shift+click for multi-select
            if (e.shiftKey) {
                setSelectedIndices(prev => {
                    const next = new Set(prev);
                    if (next.has(hit.idx)) next.delete(hit.idx);
                    else next.add(hit.idx);
                    return next;
                });
            } else {
                setSelectedIndices(new Set([hit.idx]));
            }
            return;
        }

        // Place new point
        if (path.length === 0) {
            const newPath = [{ type: 'M', x, y }];
            onGlyphChange({ ...glyph, path: newPath });
            setPenDragging(true);
            setPenDragStart({ x, y, idx: 0 });
        } else {
            // Add a line to start, will be upgraded to curve on drag
            const newPath = [...path, { type: 'L', x, y }];
            onGlyphChange({ ...glyph, path: newPath });
            setPenDragging(true);
            setPenDragStart({ x, y, idx: newPath.length - 1 });
        }
    }, [glyph, onGlyphChange, getTransform, fromCanvas, toCanvas, getFirstPoint, hitTest, mirrorMode, mirrorX]);

    // ===== PEN TOOL: mousemove during drag → create curve handles =====
    const handlePenDrag = useCallback((mx, my, canvas) => {
        if (!penDragging || !penDragStart) return;
        const t = getTransform(canvas);
        const { x, y } = fromCanvas(mx, my, t);
        const dx = x - penDragStart.x;
        const dy = y - penDragStart.y;

        // Only convert to curve if dragged a meaningful distance
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;

        const newPath = [...glyph.path];
        const idx = penDragStart.idx;
        const cmd = newPath[idx];

        if (!cmd) return; // idx out of bounds (stale closure) — bail

        if (cmd.type === 'L') {
            // Convert L to C: find previous point for handle1
            let prevX = 0, prevY = 0;
            for (let i = idx - 1; i >= 0; i--) {
                if (newPath[i].x != null) { prevX = newPath[i].x; prevY = newPath[i].y; break; }
            }
            // Symmetric handles: handle2 mirrors the drag, handle1 is auto
            newPath[idx] = {
                type: 'C',
                x1: prevX + (cmd.x - prevX) * 0.33,
                y1: prevY + (cmd.y - prevY) * 0.33,
                x2: cmd.x - dx,
                y2: cmd.y - dy,
                x: cmd.x,
                y: cmd.y,
            };
        } else if (cmd.type === 'C') {
            // Update handle2 (the one the user is dragging)
            newPath[idx] = { ...cmd, x2: cmd.x - dx, y2: cmd.y - dy };
        } else {
            // M or other — nothing to do, don't overwrite path with stale state
            return;
        }

        onGlyphChange({ ...glyph, path: newPath });
    }, [penDragging, penDragStart, glyph, onGlyphChange, getTransform, fromCanvas]);

    // ===== SELECT TOOL: drag points/handles =====
    const handleSelectDrag = useCallback((mx, my, canvas) => {
        if (dragIdx == null) return;
        const t = getTransform(canvas);
        const { x, y } = fromCanvas(mx, my, t);
        const newPath = [...glyph.path];
        const cmd = { ...newPath[dragIdx] };

        // Find mirror pair BEFORE updating the point
        const mirIdx = mirrorMode ? findMirrorIdx(dragIdx, glyph.path) : null;

        if (dragField === 'point') {
            const dx = x - cmd.x;
            const dy = y - cmd.y;

            // Multi-select: move all selected points by the same delta
            if (selectedIndices.size > 1 && selectedIndices.has(dragIdx)) {
                for (const si of selectedIndices) {
                    if (si >= 0 && si < newPath.length && newPath[si].x != null) {
                        const sc = { ...newPath[si] };
                        sc.x = Math.round(sc.x + dx);
                        sc.y = Math.round(sc.y + dy);
                        // Also move any control points if it's a curve
                        if (sc.type === 'C') {
                            sc.x1 = Math.round(sc.x1 + dx); sc.y1 = Math.round(sc.y1 + dy);
                            sc.x2 = Math.round(sc.x2 + dx); sc.y2 = Math.round(sc.y2 + dy);
                        } else if (sc.type === 'Q') {
                            sc.x1 = Math.round(sc.x1 + dx); sc.y1 = Math.round(sc.y1 + dy);
                        }
                        newPath[si] = sc;
                    }
                }
            } else {
                // Single point move
                cmd.x = x; cmd.y = y;
                newPath[dragIdx] = cmd;
                // Move mirror pair
                if (mirIdx != null) {
                    const mirCmd = { ...newPath[mirIdx] };
                    mirCmd.x = mirrorX(x);
                    mirCmd.y = y;
                    newPath[mirIdx] = mirCmd;
                }
            }
        } else if (dragField === 'handle1') {
            cmd.x1 = x; cmd.y1 = y;
            newPath[dragIdx] = cmd;
            // Mirror handle
            if (mirIdx != null && newPath[mirIdx].type === 'C') {
                const mirC = { ...newPath[mirIdx] };
                mirC.x1 = mirrorX(x); mirC.y1 = y;
                newPath[mirIdx] = mirC;
            }
        } else if (dragField === 'handle2') {
            cmd.x2 = x; cmd.y2 = y;
            newPath[dragIdx] = cmd;
            if (mirIdx != null && newPath[mirIdx].type === 'C') {
                const mirC = { ...newPath[mirIdx] };
                mirC.x2 = mirrorX(x); mirC.y2 = y;
                newPath[mirIdx] = mirC;
            }
        } else if (dragField === 'cornerRadius') {
            // Drag corner radius handle: set radius = distance from cursor to corner point
            const dist = Math.hypot(x - cmd.x, y - cmd.y);
            cmd.cornerRadius = Math.max(0, Math.round(dist));
            newPath[dragIdx] = cmd;
        }
        onGlyphChange({ ...glyph, path: newPath });
    }, [dragIdx, dragField, glyph, onGlyphChange, getTransform, fromCanvas, mirrorMode, findMirrorIdx, mirrorX, selectedIndices]);

    // === Double-click to insert point on segment ===
    const handleDoubleClick = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const t = getTransform(canvas);
        const { x, y } = fromCanvas(mx, my, t);
        const path = glyph.path || [];
        if (path.length < 2) return;

        // Find nearest segment
        let bestDist = Infinity;
        let bestIdx = -1; // index of the end-point command of the segment
        const isClosed = path[path.length - 1]?.type === 'Z';

        const pointDist = (ax, ay, bx, by, px, py) => {
            // Distance from point (px,py) to line segment (ax,ay)-(bx,by)
            const dx = bx - ax, dy = by - ay;
            const len2 = dx * dx + dy * dy;
            if (len2 < 1) return Math.hypot(px - ax, py - ay);
            let t = ((px - ax) * dx + (py - ay) * dy) / len2;
            t = Math.max(0, Math.min(1, t));
            return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
        };

        let prevX = null, prevY = null;
        for (let i = 0; i < path.length; i++) {
            const cmd = path[i];
            if (cmd.type === 'M') { prevX = cmd.x; prevY = cmd.y; continue; }
            if (cmd.type === 'Z') {
                // Closing segment: last point → first point
                if (prevX != null) {
                    const first = path.find(c => c.type === 'M');
                    if (first) {
                        const d = pointDist(prevX, prevY, first.x, first.y, x, y);
                        if (d < bestDist) { bestDist = d; bestIdx = i; }
                    }
                }
                continue;
            }
            if (cmd.x != null && prevX != null) {
                if (cmd.type === 'L') {
                    const d = pointDist(prevX, prevY, cmd.x, cmd.y, x, y);
                    if (d < bestDist) { bestDist = d; bestIdx = i; }
                } else if (cmd.type === 'C' || cmd.type === 'Q') {
                    // For curves, approximate with endpoint distance
                    const d = pointDist(prevX, prevY, cmd.x, cmd.y, x, y);
                    if (d < bestDist) { bestDist = d; bestIdx = i; }
                }
            }
            if (cmd.x != null) { prevX = cmd.x; prevY = cmd.y; }
        }

        // Threshold: 15 units in path space
        const threshold = 15 / (t.scale || 1);
        if (bestDist > threshold || bestIdx < 0) return;

        const newPath = [...path];
        const seg = newPath[bestIdx];

        if (seg.type === 'Z') {
            // Insert on the closing segment: add an L before the Z
            const firstM = path.find(c => c.type === 'M');
            let lastPt = null;
            for (let i = path.length - 1; i >= 0; i--) {
                if (path[i].x != null && path[i].type !== 'Z') { lastPt = path[i]; break; }
            }
            if (firstM && lastPt) {
                const midX = Math.round((lastPt.x + firstM.x) / 2);
                const midY = Math.round((lastPt.y + firstM.y) / 2);
                newPath.splice(bestIdx, 0, { type: 'L', x: midX, y: midY });
                onGlyphChange({ ...glyph, path: newPath });
                setSelectedIdx(bestIdx);
                setTool(TOOLS.SELECT);
            }
        } else if (seg.type === 'L') {
            // Split straight segment at midpoint
            let prevPt = null;
            for (let i = bestIdx - 1; i >= 0; i--) {
                if (newPath[i].x != null) { prevPt = newPath[i]; break; }
            }
            if (prevPt) {
                const midX = Math.round((prevPt.x + seg.x) / 2);
                const midY = Math.round((prevPt.y + seg.y) / 2);
                newPath.splice(bestIdx, 0, { type: 'L', x: midX, y: midY });
                onGlyphChange({ ...glyph, path: newPath });
                setSelectedIdx(bestIdx);
                setTool(TOOLS.SELECT);
            }
        } else if (seg.type === 'C') {
            // Split cubic at t=0.5 using De Casteljau
            let prevPt = null;
            for (let i = bestIdx - 1; i >= 0; i--) {
                if (newPath[i].x != null) { prevPt = newPath[i]; break; }
            }
            if (prevPt) {
                const p0 = { x: prevPt.x, y: prevPt.y };
                const p1 = { x: seg.x1, y: seg.y1 };
                const p2 = { x: seg.x2, y: seg.y2 };
                const p3 = { x: seg.x, y: seg.y };
                const mid = (a, b) => Math.round((a + b) / 2);
                const q0 = { x: mid(p0.x, p1.x), y: mid(p0.y, p1.y) };
                const q1 = { x: mid(p1.x, p2.x), y: mid(p1.y, p2.y) };
                const q2 = { x: mid(p2.x, p3.x), y: mid(p2.y, p3.y) };
                const r0 = { x: mid(q0.x, q1.x), y: mid(q0.y, q1.y) };
                const r1 = { x: mid(q1.x, q2.x), y: mid(q1.y, q2.y) };
                const s = { x: mid(r0.x, r1.x), y: mid(r0.y, r1.y) };
                // First half: C(q0, r0, s)
                const c1 = { type: 'C', x1: q0.x, y1: q0.y, x2: r0.x, y2: r0.y, x: s.x, y: s.y };
                // Second half: C(r1, q2, p3)
                const c2 = { type: 'C', x1: r1.x, y1: r1.y, x2: q2.x, y2: q2.y, x: p3.x, y: p3.y };
                newPath.splice(bestIdx, 1, c1, c2);
                onGlyphChange({ ...glyph, path: newPath });
                setSelectedIdx(bestIdx);
                setTool(TOOLS.SELECT);
            }
        }
    }, [glyph, onGlyphChange, getTransform, fromCanvas]);

    // === Unified mouse handlers ===
    const handleMouseDown = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        // Middle-click or Space+click → start panning
        if (e.button === 1 || spaceHeld) {
            setIsPanning(true);
            setPanStart({ x: e.clientX, y: e.clientY, px: panX, py: panY });
            e.preventDefault();
            return;
        }

        if (tool === TOOLS.PEN) {
            handlePenDown(mx, my, canvas, e);
        } else {
            const hit = hitTest(mx, my);
            if (hit) {
                setDragIdx(hit.idx);
                setDragField(hit.field);
                // Only change selection when clicking an on-curve point, not a handle
                if (hit.field === 'point') {
                    setSelectedIdx(hit.idx);
                    // Shift+click for multi-select
                    if (e.shiftKey) {
                        setSelectedIndices(prev => {
                            const next = new Set(prev);
                            if (next.has(hit.idx)) next.delete(hit.idx);
                            else next.add(hit.idx);
                            return next;
                        });
                    } else if (!selectedIndices.has(hit.idx)) {
                        // Only reset selection if clicking a point NOT already selected
                        setSelectedIndices(new Set([hit.idx]));
                    }
                    // If clicking a point already in multi-selection, keep it (for dragging)
                }
            }
            else { setSelectedIdx(null); setSelectedIndices(new Set()); }
        }
    }, [tool, handlePenDown, hitTest, spaceHeld, panX, panY, selectedIndices]);

    const handleMouseMove = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        setCursorPos({ cx: mx, cy: my });

        // Panning
        if (isPanning && panStart) {
            setPanX(panStart.px + (e.clientX - panStart.x));
            setPanY(panStart.py + (e.clientY - panStart.y));
            return;
        }

        if (tool === TOOLS.PEN) {
            handlePenDrag(mx, my, canvas);
            // Also allow dragging existing nodes in pen mode (when not placing new points)
            if (!penDragging && dragIdx != null) {
                handleSelectDrag(mx, my, canvas);
            }
        } else {
            handleSelectDrag(mx, my, canvas);
        }
    }, [tool, handlePenDrag, handleSelectDrag, isPanning, panStart, penDragging, dragIdx]);

    const handleMouseUp = useCallback(() => {
        setPenDragging(false);
        setPenDragStart(null);
        setDragIdx(null);
        setDragField(null);
        setIsPanning(false);
        setPanStart(null);
    }, []);

    // === Scroll wheel zoom ===
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        // Zoom factor
        const delta = e.deltaY > 0 ? 0.97 : 1.03;
        const newZoom = Math.max(0.1, Math.min(20, zoom * delta));

        // Zoom towards cursor position
        const zoomRatio = newZoom / zoom;
        setPanX(prev => mx - zoomRatio * (mx - prev));
        setPanY(prev => my - zoomRatio * (my - prev));
        setZoom(newZoom);
    }, [zoom]);

    // Attach wheel listener (non-passive to allow preventDefault)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.addEventListener('wheel', handleWheel, { passive: false });
        return () => canvas.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    // --- Actions ---
    const deleteSelected = useCallback(() => {
        if (selectedIdx == null) return;
        const path = glyph.path || [];
        const cmd = path[selectedIdx];
        if (!cmd) return;

        // Find the contour boundaries for the selected index
        // A contour goes from an M command to the next M or end of path (possibly via Z)
        let contourStart = selectedIdx;
        for (let i = selectedIdx; i >= 0; i--) {
            if (path[i].type === 'M') { contourStart = i; break; }
        }
        let contourEnd = path.length - 1; // inclusive
        for (let i = selectedIdx + 1; i < path.length; i++) {
            if (path[i].type === 'M') { contourEnd = i - 1; break; }
        }

        // Count real points (non-Z) in this contour
        let pointCount = 0;
        for (let i = contourStart; i <= contourEnd; i++) {
            if (path[i].type !== 'Z') pointCount++;
        }

        let newPath;
        if (cmd.type === 'M' || pointCount <= 2) {
            // Deleting the M (contour start) or contour would be degenerate:
            // remove the entire contour
            newPath = [...path.slice(0, contourStart), ...path.slice(contourEnd + 1)];
        } else {
            // Normal deletion within a contour — just remove this one element
            newPath = path.filter((_, i) => i !== selectedIdx);
        }

        setSelectedIdx(null);
        onGlyphChange({ ...glyph, path: newPath });
    }, [selectedIdx, glyph, onGlyphChange]);

    const closePath = useCallback(() => {
        const path = glyph.path || [];
        if (path.length > 1 && path[path.length - 1].type !== 'Z')
            onGlyphChange({ ...glyph, path: [...path, { type: 'Z' }] });
    }, [glyph, onGlyphChange]);

    const convertToCurve = useCallback(() => {
        if (selectedIdx == null) return;
        const path = [...glyph.path];
        const cmd = path[selectedIdx];
        if (cmd.x == null) return;

        // Find prev point
        let prevX = 0, prevY = 0;
        for (let i = selectedIdx - 1; i >= 0; i--) {
            if (path[i].x != null) { prevX = path[i].x; prevY = path[i].y; break; }
        }
        // Find next point
        let nextIdx = null, nextX = cmd.x, nextY = cmd.y;
        for (let i = selectedIdx + 1; i < path.length; i++) {
            if (path[i].x != null && path[i].type !== 'Z') { nextIdx = i; nextX = path[i].x; nextY = path[i].y; break; }
            if (path[i].type === 'Z') break;
        }

        // Convert incoming segment (current cmd) to curve if it's L
        if (cmd.type === 'L') {
            const dx = cmd.x - prevX;
            const dy = cmd.y - prevY;
            path[selectedIdx] = {
                type: 'C',
                x1: Math.round(prevX + dx * 0.33),
                y1: Math.round(prevY + dy * 0.33),
                x2: Math.round(cmd.x - dx * 0.33),
                y2: Math.round(cmd.y - dy * 0.33),
                x: cmd.x, y: cmd.y,
            };
        }

        // Convert outgoing segment (next cmd) to curve if it's L
        if (nextIdx != null && path[nextIdx].type === 'L') {
            const nc = path[nextIdx];
            const dx = nc.x - cmd.x;
            const dy = nc.y - cmd.y;
            path[nextIdx] = {
                type: 'C',
                x1: Math.round(cmd.x + dx * 0.33),
                y1: Math.round(cmd.y + dy * 0.33),
                x2: Math.round(nc.x - dx * 0.33),
                y2: Math.round(nc.y - dy * 0.33),
                x: nc.x, y: nc.y,
            };
        }

        onGlyphChange({ ...glyph, path });
    }, [selectedIdx, glyph, onGlyphChange]);

    const convertToLine = useCallback(() => {
        if (selectedIdx == null) return;
        const path = [...glyph.path];
        const cmd = path[selectedIdx];
        // Convert incoming segment to line
        if (cmd.type === 'C' || cmd.type === 'Q') {
            path[selectedIdx] = { type: 'L', x: cmd.x, y: cmd.y };
        }
        // Also convert outgoing segment to line
        for (let i = selectedIdx + 1; i < path.length; i++) {
            if (path[i].type === 'Z') break;
            if (path[i].x != null) {
                if (path[i].type === 'C' || path[i].type === 'Q') {
                    path[i] = { type: 'L', x: path[i].x, y: path[i].y };
                }
                break;
            }
        }
        onGlyphChange({ ...glyph, path });
    }, [selectedIdx, glyph, onGlyphChange]);

    const handleAdvanceWidthChange = useCallback((val) => {
        setAdvanceWidth(val);
        onGlyphChange({ ...glyph, advanceWidth: val });
    }, [glyph, onGlyphChange]);

    // --- Keyboard shortcuts ---
    useEffect(() => {
        const handler = (e) => {
            // Space for panning
            if (e.key === ' ' && !e.repeat) { e.preventDefault(); setSpaceHeld(true); return; }
            if (e.key === 'p' || e.key === 'P') setTool(TOOLS.PEN);
            if (e.key === 'v' || e.key === 'V') setTool(TOOLS.SELECT);
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIdx != null) {
                // Don't delete node when user is editing an input field
                const tag = document.activeElement?.tagName?.toLowerCase();
                if (tag === 'input' || tag === 'textarea') return;
                e.preventDefault();
                deleteSelected();
            }
            if (e.key === 'g' || e.key === 'G') setShowGrid(p => !p);
            if (e.key === 's' || e.key === 'S') setShowSymmetry(p => !p);
            if (e.key === 'n' || e.key === 'N') setSnapToGrid(p => !p);
            if (e.key === 'm') { setMirrorMode(p => { if (!p) setShowSymmetry(true); return !p; }); }
            if (e.key === '0' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setZoom(1); setPanX(0); setPanY(0); }
            if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                const path = glyph?.path || [];
                const all = new Set();
                for (let i = 0; i < path.length; i++) {
                    if (path[i].x != null && path[i].type !== 'Z') all.add(i);
                }
                setSelectedIndices(all);
                if (all.size > 0) setSelectedIdx([...all][0]);
                setTool(TOOLS.SELECT);
            }
            if (e.key === 'Escape') { setSelectedIdx(null); setSelectedIndices(new Set()); setPenDragging(false); }
        };
        const upHandler = (e) => {
            if (e.key === ' ') { setSpaceHeld(false); setIsPanning(false); setPanStart(null); }
        };
        window.addEventListener('keydown', handler);
        window.addEventListener('keyup', upHandler);
        return () => { window.removeEventListener('keydown', handler); window.removeEventListener('keyup', upHandler); };
    }, [selectedIdx, deleteSelected]);

    const toolBtn = (id, icon, label, shortcut) => (
        <Tooltip title={`${label} (${shortcut})`}>
            <Button type={tool === id ? 'primary' : 'text'} icon={icon} size="small"
                onClick={() => setTool(id)}
                style={{
                    color: tool === id ? '#fff' : '#8b949e',
                    background: tool === id ? '#1f6feb' : 'transparent',
                    border: '1px solid #30363d', width: 32, height: 32,
                }}
            />
        </Tooltip>
    );

    const metricRow = (label, value, color, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#c9d1d9', flex: 1 }}>{label}</span>
            <InputNumber size="small" value={value} onChange={onChange}
                style={{ width: 65, background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
            />
        </div>
    );

    const selCmd = selectedIdx != null && glyph.path?.[selectedIdx];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Toolbar */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderBottom: '1px solid #30363d',
                background: '#161b22', flexShrink: 0, flexWrap: 'wrap',
            }}>
                <Button type="text" size="small" onClick={onBack}
                    style={{ color: '#8b949e', border: '1px solid #30363d', marginRight: 4 }}>← Back</Button>
                {toolBtn(TOOLS.SELECT, <DragOutlined />, 'Select / Move points', 'V')}
                {toolBtn(TOOLS.PEN, <EditOutlined />, 'Pen: click=corner, drag=curve', 'P')}
                <div style={{ width: 1, height: 20, background: '#30363d' }} />
                <Tooltip title="Close path (connect to first point)">
                    <Button type="text" size="small" onClick={closePath}
                        style={{ color: '#8b949e', border: '1px solid #30363d', fontSize: 11 }}>⬡ Close</Button>
                </Tooltip>
                <Tooltip title="Convert selected point to curve">
                    <Button type="text" size="small" onClick={convertToCurve}
                        disabled={!selCmd || selCmd.type !== 'L'}
                        style={{ color: '#8b949e', border: '1px solid #30363d', fontSize: 11 }}>⌒ Curve</Button>
                </Tooltip>
                <Tooltip title="Round selected corner">
                    <Button type="text" size="small"
                        disabled={!selCmd || (selCmd.type !== 'L' && selCmd.type !== 'M')}
                        onClick={() => {
                            if (selectedIdx == null) return;
                            const path = [...glyph.path];
                            const cmd = path[selectedIdx];
                            const newRadius = cmd.cornerRadius > 0 ? 0 : 50;
                            path[selectedIdx] = { ...cmd, cornerRadius: newRadius };
                            onGlyphChange({ ...glyph, path });
                        }}
                        style={{
                            color: selCmd?.cornerRadius > 0 ? '#d29922' : '#8b949e',
                            border: '1px solid #30363d', fontSize: 11,
                            background: selCmd?.cornerRadius > 0 ? '#d2992215' : 'transparent',
                        }}>◉ Round</Button>
                </Tooltip>
                <Tooltip title="Convert selected curve to straight line">
                    <Button type="text" size="small" onClick={convertToLine}
                        disabled={!selCmd || (selCmd.type !== 'C' && selCmd.type !== 'Q')}
                        style={{ color: '#8b949e', border: '1px solid #30363d', fontSize: 11 }}>╱ Line</Button>
                </Tooltip>
                <Tooltip title="Delete selected point (Del)">
                    <Button type="text" size="small" icon={<DeleteOutlined />} onClick={deleteSelected}
                        disabled={selectedIdx == null}
                        style={{ color: selectedIdx != null ? '#f85149' : '#484f58', border: '1px solid #30363d' }} />
                </Tooltip>
                <Tooltip title="Clear all points">
                    <Button type="text" size="small" icon={<UndoOutlined />}
                        onClick={() => onGlyphChange({ ...glyph, path: [] })}
                        style={{ color: '#8b949e', border: '1px solid #30363d' }} />
                </Tooltip>

                {/* Save Accent */}
                {(selectedIdx != null || selectedIndices.size > 0) && !accentNaming && (
                    <>
                        <div style={{ width: 1, height: 20, background: '#30363d' }} />
                        <Tooltip title="Save selected nodes as reusable accent (shift+click to multi-select)">
                            <Button type="text" size="small" icon={<ScissorOutlined />}
                                onClick={() => { setAccentNaming(true); setAccentName(''); }}
                                style={{ color: '#da8ee7', border: '1px solid #30363d', fontSize: 11 }}>✂ Save Accent{selectedIndices.size > 1 ? ` (${selectedIndices.size})` : ''}</Button>
                        </Tooltip>
                    </>
                )}
                {accentNaming && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#21262d', borderRadius: 6, padding: '2px 6px', border: '1px solid #da8ee744' }}>
                        <Input
                            size="small"
                            placeholder="Accent name..."
                            value={accentName}
                            onChange={(e) => setAccentName(e.target.value)}
                            onPressEnter={() => {
                                if (!accentName.trim()) return;
                                const path = glyph.path || [];
                                // Use exactly the selected indices
                                const sel = selectedIndices.size > 0 ? [...selectedIndices] : (selectedIdx != null ? [selectedIdx] : []);
                                if (sel.length === 0) return;
                                const sorted = [...sel].sort((a, b) => a - b);
                                const normalized = normalizeAccentPath(path, sorted);
                                if (normalized.length === 0) return;
                                const newAccent = {
                                    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
                                    name: accentName.trim(),
                                    path: normalized,
                                };
                                onAccentsChange([...accents, newAccent]);
                                setAccentNaming(false);
                                setAccentName('');
                            }}
                            style={{ width: 120, background: '#0d1117', border: '1px solid #30363d', fontSize: 11, color: '#e6edf3' }}
                            autoFocus
                        />
                        <Button size="small" type="text"
                            onClick={() => {
                                if (!accentName.trim()) return;
                                const path = glyph.path || [];
                                const sel = selectedIndices.size > 0 ? [...selectedIndices] : (selectedIdx != null ? [selectedIdx] : []);
                                if (sel.length === 0) return;
                                const sorted = [...sel].sort((a, b) => a - b);
                                const normalized = normalizeAccentPath(path, sorted);
                                if (normalized.length === 0) return;
                                const newAccent = {
                                    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
                                    name: accentName.trim(),
                                    path: normalized,
                                };
                                onAccentsChange([...accents, newAccent]);
                                setAccentNaming(false);
                                setAccentName('');
                            }}
                            style={{ color: '#3fb950', fontSize: 11, padding: '0 6px' }}>Save</Button>
                        <Button size="small" type="text"
                            onClick={() => { setAccentNaming(false); setAccentName(''); }}
                            style={{ color: '#f85149', fontSize: 11, padding: '0 4px' }}>✕</Button>
                    </div>
                )}
                <div style={{ width: 1, height: 20, background: '#30363d' }} />
                <Tooltip title="Toggle grid (G)">
                    <Button type={showGrid ? 'primary' : 'text'} size="small" icon={<BorderOutlined />}
                        onClick={() => setShowGrid(p => !p)}
                        style={{
                            color: showGrid ? '#fff' : '#8b949e',
                            background: showGrid ? '#1f6feb' : 'transparent',
                            border: '1px solid #30363d', width: 32, height: 32,
                        }} />
                </Tooltip>
                {showGrid && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 10, color: '#484f58' }}>Grid</span>
                        <InputNumber min={10} max={200} step={10} value={gridSize}
                            onChange={v => setGridSize(v || 50)}
                            size="small" style={{ width: 55, background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }} />
                    </div>
                )}
                <Tooltip title={`Snap to grid (N) — ${snapToGrid ? 'ON' : 'OFF'}`}>
                    <Button type={snapToGrid ? 'primary' : 'text'} size="small"
                        onClick={() => setSnapToGrid(p => !p)}
                        style={{
                            color: snapToGrid ? '#fff' : '#8b949e',
                            background: snapToGrid ? '#f0883e' : 'transparent',
                            border: '1px solid #30363d', height: 32, fontSize: 10, fontWeight: 600,
                        }}>⊞ Snap</Button>
                </Tooltip>
                <Tooltip title="Symmetry line (S)">
                    <Button type={showSymmetry ? 'primary' : 'text'} size="small"
                        onClick={() => setShowSymmetry(p => !p)}
                        style={{
                            color: showSymmetry ? '#fff' : '#8b949e',
                            background: showSymmetry ? '#a371f7' : 'transparent',
                            border: '1px solid #30363d', height: 32, fontSize: 11, fontWeight: 600,
                        }}>⎸</Button>
                </Tooltip>
                <Tooltip title={`Mirror mode (M) — ${mirrorMode ? 'ON: actions mirror across symmetry axis' : 'OFF'}`}>
                    <Button type={mirrorMode ? 'primary' : 'text'} size="small"
                        onClick={() => { setMirrorMode(p => { if (!p) setShowSymmetry(true); return !p; }); }}
                        style={{
                            color: mirrorMode ? '#fff' : '#8b949e',
                            background: mirrorMode ? '#a371f7' : 'transparent',
                            border: '1px solid #30363d', height: 32, fontSize: 10, fontWeight: 600,
                        }}>⋈ Mirror</Button>
                </Tooltip>
                <div style={{ width: 1, height: 20, background: '#30363d' }} />
                <Tooltip title="Toggle metrics panel">
                    <Button type={showMetrics ? 'primary' : 'text'} size="small" icon={<ColumnHeightOutlined />}
                        onClick={() => setShowMetrics(p => !p)}
                        style={{
                            color: showMetrics ? '#fff' : '#8b949e',
                            background: showMetrics ? '#1f6feb' : 'transparent',
                            border: '1px solid #30363d', width: 32, height: 32,
                        }} />
                </Tooltip>
                <Tooltip title="Toggle glyph context strip">
                    <Button type={showContext ? 'primary' : 'text'} size="small" icon={<FontSizeOutlined />}
                        onClick={() => setShowContext(p => !p)}
                        style={{
                            color: showContext ? '#fff' : '#8b949e',
                            background: showContext ? '#1f6feb' : 'transparent',
                            border: '1px solid #30363d', width: 32, height: 32,
                        }} />
                </Tooltip>

                <div style={{ flex: 1 }} />

                {/* Hint text */}
                <div style={{ fontSize: 10, color: '#484f58', marginRight: 8 }}>
                    {tool === TOOLS.PEN
                        ? `Click = corner · Drag = curve · Click first pt = close${snapToGrid ? ' · ⊞ Snap ON' : ''}`
                        : `Click to select · Drag to move · Del to delete${snapToGrid ? ' · ⊞ Snap ON' : ''}`}
                </div>

                {/* Glyph info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontSize: 28, color: '#e6edf3', fontWeight: 700, lineHeight: 1 }}>
                        {glyph.unicode >= 33 ? String.fromCharCode(glyph.unicode) : '␣'}
                    </div>
                    <div>
                        <div style={{ fontSize: 11, color: '#8b949e' }}>{glyph.name}</div>
                        <div style={{ fontSize: 10, color: '#484f58', fontFamily: 'monospace' }}>{unicodeLabel(glyph.unicode)}</div>
                    </div>
                </div>
            </div>

            {/* Canvas + Metrics */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{
                    flex: 1, background: '#0d1117', position: 'relative',
                    cursor: isPanning ? 'grabbing' : spaceHeld ? 'grab' : tool === TOOLS.PEN ? 'crosshair' : 'default',
                }}>
                    <canvas ref={canvasRef}
                        style={{ width: '100%', height: '100%', display: 'block' }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onDoubleClick={handleDoubleClick}
                        onMouseLeave={() => { handleMouseUp(); setCursorPos(null); }}
                    />
                    {/* Zoom indicator */}
                    <div style={{
                        position: 'absolute', bottom: showContext ? 76 : 12, left: 12,
                        background: '#161b22ee', border: '1px solid #30363d',
                        borderRadius: 6, padding: '4px 10px',
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 11, color: '#8b949e', userSelect: 'none',
                        transition: 'bottom 0.15s',
                    }}>
                        <span style={{ fontFamily: 'monospace', minWidth: 45 }}>{Math.round(zoom * 100)}%</span>
                        {(zoom !== 1 || panX !== 0 || panY !== 0) && (
                            <button
                                onClick={() => { setZoom(1); setPanX(0); setPanY(0); }}
                                style={{
                                    background: '#30363d', border: 'none', color: '#c9d1d9',
                                    borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
                                    fontSize: 10,
                                }}
                            >Reset</button>
                        )}
                    </div>

                    {/* Glyph Context Strip */}
                    {showContext && (() => {
                        const drawnGlyphs = allGlyphs.filter(g => g.path && g.path.length > 0);
                        if (drawnGlyphs.length === 0) return null;
                        const ctxPathToD = (path) => {
                            if (!path || path.length === 0) return '';
                            const dp = buildRoundedPath(path);
                            return dp.map(cmd => {
                                switch (cmd.type) {
                                    case 'M': return `M ${cmd.x} ${cmd.y}`;
                                    case 'L': return `L ${cmd.x} ${cmd.y}`;
                                    case 'C': return `C ${cmd.x1} ${cmd.y1} ${cmd.x2} ${cmd.y2} ${cmd.x} ${cmd.y}`;
                                    case 'Q': return `Q ${cmd.x1} ${cmd.y1} ${cmd.x} ${cmd.y}`;
                                    case 'Z': return 'Z';
                                    default: return '';
                                }
                            }).join(' ');
                        };
                        return (
                            <div ref={contextStripRef} style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                height: 64, background: '#161b22ee',
                                borderTop: '1px solid #30363d',
                                backdropFilter: 'blur(8px)',
                                display: 'flex', alignItems: 'center',
                                overflowX: 'auto', overflowY: 'hidden',
                                padding: '0 12px', gap: 6,
                                zIndex: 10,
                            }}>
                                {drawnGlyphs.map(g => {
                                    const isCurrent = g.unicode === glyph.unicode;
                                    const d = ctxPathToD(g.path);
                                    const aw = g.advanceWidth || 600;
                                    const pad = 40;
                                    const vbX = -pad;
                                    const vbY = -800 - pad;
                                    const vbW = Math.max(aw, 400) + pad * 2;
                                    const vbH = 1000 + pad * 2;
                                    return (
                                        <Tooltip key={g.unicode} title={`${g.name} — click to edit`} mouseEnterDelay={0.4}>
                                            <div
                                                onClick={() => onSwitchGlyph && onSwitchGlyph(g)}
                                                style={{
                                                    flexShrink: 0,
                                                    width: 48, height: 48,
                                                    borderRadius: 6,
                                                    background: isCurrent ? '#1f6feb22' : '#0d1117',
                                                    border: isCurrent ? '2px solid #1f6feb' : '1px solid #30363d',
                                                    display: 'flex', flexDirection: 'column',
                                                    alignItems: 'center', justifyContent: 'center',
                                                    cursor: isCurrent ? 'default' : 'pointer',
                                                    transition: 'border-color 0.15s, background 0.15s',
                                                    position: 'relative',
                                                }}
                                            >
                                                <svg width="32" height="32" viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
                                                    style={{ overflow: 'visible' }}>
                                                    <g transform="scale(1, -1)">
                                                        <path d={d}
                                                            fill={isCurrent ? '#58a6ff44' : '#58a6ff22'}
                                                            stroke={isCurrent ? '#58a6ff' : '#8b949e'}
                                                            strokeWidth={Math.max(vbW / 50, 10)}
                                                            fillRule="evenodd" />
                                                    </g>
                                                </svg>
                                                <div style={{
                                                    position: 'absolute', bottom: 1,
                                                    fontSize: 7, color: isCurrent ? '#58a6ff' : '#484f58',
                                                    fontFamily: 'monospace', fontWeight: isCurrent ? 600 : 400,
                                                    lineHeight: 1,
                                                }}>
                                                    {g.unicode >= 33 ? String.fromCharCode(g.unicode) : '␣'}
                                                </div>
                                            </div>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>

                {showMetrics && (
                    <div style={{
                        width: 220, flexShrink: 0, borderLeft: '1px solid #30363d',
                        background: '#161b22', padding: 16, overflowY: 'auto',
                    }}>
                        <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 10 }}>FONT METRICS</div>
                        {metricRow('Ascender', ascender, '#1f6feb', v => onMetadataChange({ ...metadata, ascender: v }))}
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: -4, marginBottom: 8, lineHeight: 1.3 }}>
                            Top of tallest letters like b, d, h. Defines the upper boundary of your font.
                        </div>
                        {metricRow('Cap Height', capHeight, '#d29922', v => onMetadataChange({ ...metadata, capHeight: v }))}
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: -4, marginBottom: 8, lineHeight: 1.3 }}>
                            Top of flat capital letters like H, E, T. Usually slightly below ascender.
                        </div>
                        {metricRow('x-Height', xHeight, '#3fb950', v => onMetadataChange({ ...metadata, xHeight: v }))}
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: -4, marginBottom: 8, lineHeight: 1.3 }}>
                            Top of lowercase flat letters like x, a, e. The most important metric — defines readability.
                        </div>
                        {metricRow('Baseline', 0, '#8b949e', () => { })}
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: -4, marginBottom: 8, lineHeight: 1.3 }}>
                            Where letters sit. Always 0. All vertical positions are relative to this line.
                        </div>
                        {metricRow('Descender', descender, '#f85149', v => onMetadataChange({ ...metadata, descender: v }))}
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: -4, marginBottom: 8, lineHeight: 1.3 }}>
                            Bottom of letters with tails like g, p, y. A negative value below the baseline.
                        </div>

                        <div style={{ height: 1, background: '#30363d', margin: '10px 0' }} />
                        <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 8 }}>GLYPH</div>
                        {metricRow('Adv. Width', advanceWidth, '#da3633', handleAdvanceWidthChange)}
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: -4, marginBottom: 6, lineHeight: 1.3 }}>
                            Total horizontal space this glyph occupies, including sidebearings. Controls letter spacing.
                        </div>
                        <div style={{ fontSize: 10, color: '#484f58', marginTop: 4 }}>
                            Points: {(glyph.path || []).filter(c => c.type !== 'Z').length}
                        </div>
                        <div style={{ fontSize: 10, color: '#484f58' }}>
                            Contours: {(glyph.path || []).filter(c => c.type === 'M').length}
                        </div>
                        {selCmd && selCmd.x != null && (
                            <>
                                <div style={{ height: 1, background: '#30363d', margin: '10px 0' }} />
                                <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 8 }}>SELECTED POINT</div>

                                {/* Node type */}
                                <div style={{ fontSize: 10, color: '#484f58', marginBottom: 4 }}>Type</div>
                                <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                                    {(() => {
                                        // Check if either side is curved
                                        const path = glyph.path || [];
                                        const inIsCurve = selCmd.type === 'C';
                                        let outIsCurve = false;
                                        for (let i = selectedIdx + 1; i < path.length; i++) {
                                            if (path[i].type === 'Z') break;
                                            if (path[i].type === 'C') { outIsCurve = true; break; }
                                            if (path[i].x != null) break;
                                        }
                                        const isCurved = inIsCurve || outIsCurve;
                                        const isStraight = !isCurved;
                                        const isStart = selCmd.type === 'M';
                                        return (
                                            <>
                                                <Button size="small"
                                                    type={isStraight || isStart ? 'primary' : 'default'}
                                                    onClick={convertToLine}
                                                    disabled={isStart || isStraight}
                                                    style={{
                                                        flex: 1, fontSize: 10, height: 26,
                                                        background: (isStraight || isStart) ? '#1f6feb' : '#21262d',
                                                        border: '1px solid #30363d',
                                                        color: (isStraight || isStart) ? '#fff' : '#8b949e',
                                                    }}>╱ Straight</Button>
                                                <Button size="small"
                                                    type={isCurved ? 'primary' : 'default'}
                                                    onClick={convertToCurve}
                                                    disabled={isStart}
                                                    style={{
                                                        flex: 1, fontSize: 10, height: 26,
                                                        background: isCurved ? '#1f6feb' : '#21262d',
                                                        border: '1px solid #30363d',
                                                        color: isCurved ? '#fff' : '#8b949e',
                                                    }}>◠ Curved</Button>
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Editable X / Y */}
                                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 9, color: '#484f58', marginBottom: 2 }}>X</div>
                                        <InputNumber
                                            value={selCmd.x} size="small"
                                            onChange={(v) => {
                                                if (v == null || selectedIdx == null) return;
                                                const path = [...glyph.path];
                                                path[selectedIdx] = { ...path[selectedIdx], x: v };
                                                onGlyphChange({ ...glyph, path });
                                            }}
                                            style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 9, color: '#484f58', marginBottom: 2 }}>Y</div>
                                        <InputNumber
                                            value={selCmd.y} size="small"
                                            onChange={(v) => {
                                                if (v == null || selectedIdx == null) return;
                                                const path = [...glyph.path];
                                                path[selectedIdx] = { ...path[selectedIdx], y: v };
                                                onGlyphChange({ ...glyph, path });
                                            }}
                                            style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                        />
                                    </div>
                                </div>

                                {/* Handle coordinates — incoming (x2,y2 of this C) */}
                                {selCmd.type === 'C' && (
                                    <>
                                        <div style={{ fontSize: 9, color: '#3fb950', marginTop: 6, marginBottom: 4 }}>← Left Handle (incoming)</div>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 9, color: '#484f58', marginBottom: 2 }}>X</div>
                                                <InputNumber value={selCmd.x2} size="small"
                                                    onChange={(v) => {
                                                        if (v == null || selectedIdx == null) return;
                                                        const path = [...glyph.path];
                                                        path[selectedIdx] = { ...path[selectedIdx], x2: v };
                                                        onGlyphChange({ ...glyph, path });
                                                    }}
                                                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 9, color: '#484f58', marginBottom: 2 }}>Y</div>
                                                <InputNumber value={selCmd.y2} size="small"
                                                    onChange={(v) => {
                                                        if (v == null || selectedIdx == null) return;
                                                        const path = [...glyph.path];
                                                        path[selectedIdx] = { ...path[selectedIdx], y2: v };
                                                        onGlyphChange({ ...glyph, path });
                                                    }}
                                                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Handle coordinates — outgoing (x1,y1 of next C) */}
                                {(() => {
                                    if (selectedIdx == null) return null;
                                    const path = glyph.path || [];
                                    let nextCIdx = null;
                                    for (let i = selectedIdx + 1; i < path.length; i++) {
                                        if (path[i].type === 'Z') break;
                                        if (path[i].type === 'C') { nextCIdx = i; break; }
                                        if (path[i].x != null) break;
                                    }
                                    if (nextCIdx == null) return null;
                                    const nextC = path[nextCIdx];
                                    return (
                                        <>
                                            <div style={{ fontSize: 9, color: '#3fb950', marginBottom: 4 }}>→ Right Handle (outgoing)</div>
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 9, color: '#484f58', marginBottom: 2 }}>X</div>
                                                    <InputNumber value={nextC.x1} size="small"
                                                        onChange={(v) => {
                                                            if (v == null) return;
                                                            const p = [...glyph.path];
                                                            p[nextCIdx] = { ...p[nextCIdx], x1: v };
                                                            onGlyphChange({ ...glyph, path: p });
                                                        }}
                                                        style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 9, color: '#484f58', marginBottom: 2 }}>Y</div>
                                                    <InputNumber value={nextC.y1} size="small"
                                                        onChange={(v) => {
                                                            if (v == null) return;
                                                            const p = [...glyph.path];
                                                            p[nextCIdx] = { ...p[nextCIdx], y1: v };
                                                            onGlyphChange({ ...glyph, path: p });
                                                        }}
                                                        style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* Corner Radius */}
                                {(selCmd.type === 'L' || selCmd.type === 'M') && (
                                    <>
                                        <div style={{ height: 1, background: '#30363d', margin: '10px 0' }} />
                                        <div style={{ color: '#d29922', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 8 }}>CORNER RADIUS</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                            <Slider
                                                min={0} max={500} step={1}
                                                value={selCmd.cornerRadius || 0}
                                                onChange={(v) => {
                                                    if (selectedIdx == null) return;
                                                    const path = [...glyph.path];
                                                    path[selectedIdx] = { ...path[selectedIdx], cornerRadius: v };
                                                    onGlyphChange({ ...glyph, path });
                                                }}
                                                style={{ flex: 1, margin: 0 }}
                                                trackStyle={{ background: '#d29922' }}
                                                handleStyle={{ borderColor: '#d29922', backgroundColor: '#0d1117' }}
                                            />
                                            <InputNumber
                                                min={0} max={500} step={1}
                                                value={selCmd.cornerRadius || 0}
                                                size="small"
                                                onChange={(v) => {
                                                    if (v == null || selectedIdx == null) return;
                                                    const path = [...glyph.path];
                                                    path[selectedIdx] = { ...path[selectedIdx], cornerRadius: v };
                                                    onGlyphChange({ ...glyph, path });
                                                }}
                                                style={{ width: 60, background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                                            />
                                        </div>
                                        <div style={{ fontSize: 9, color: '#484f58', lineHeight: 1.3 }}>
                                            Rounds this corner with a smooth arc. Only works on straight-line corners.
                                        </div>
                                        <Button size="small"
                                            onClick={() => {
                                                const r = selCmd.cornerRadius || 50;
                                                const path = glyph.path.map(cmd => {
                                                    if (cmd.type === 'L' || cmd.type === 'M') {
                                                        return { ...cmd, cornerRadius: r };
                                                    }
                                                    return cmd;
                                                });
                                                onGlyphChange({ ...glyph, path });
                                            }}
                                            style={{
                                                width: '100%', fontSize: 10, marginTop: 8,
                                                background: '#21262d', border: '1px solid #d2992244',
                                                color: '#d29922',
                                            }}>Apply {selCmd.cornerRadius || 50} to All Corners</Button>
                                    </>
                                )}

                                {/* Delete point */}
                                <Button size="small" danger onClick={deleteSelected}
                                    style={{
                                        width: '100%', fontSize: 10, marginTop: 8,
                                        background: '#21262d', border: '1px solid #f8514944',
                                    }}>Delete Point</Button>
                            </>
                        )}

                        <div style={{ height: 1, background: '#30363d', margin: '10px 0' }} />
                        <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 8 }}>UNITS PER EM</div>
                        <InputNumber
                            value={upm}
                            onChange={(v) => onMetadataChange({ ...metadata, unitsPerEm: v })}
                            min={100} max={4096}
                            style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d' }}
                        />
                        <div style={{ fontSize: 9, color: '#484f58', marginTop: 4, lineHeight: 1.3 }}>
                            The coordinate grid resolution. 1000 is standard for CFF fonts, 2048 for TrueType. Higher = more precision.
                        </div>

                        {/* ====== ACCENTS PANEL ====== */}
                        <div style={{ height: 1, background: '#30363d', margin: '12px 0' }} />
                        <div
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }}
                            onClick={() => setShowAccents(p => !p)}
                        >
                            <div style={{ color: '#da8ee7', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
                                ACCENTS {accents.length > 0 && <span style={{ color: '#484f58', fontWeight: 400 }}>({accents.length})</span>}
                            </div>
                            <span style={{ color: '#484f58', fontSize: 10 }}>{showAccents ? '▾' : '▸'}</span>
                        </div>
                        {showAccents && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {accents.length === 0 && (
                                    <div style={{ fontSize: 10, color: '#484f58', fontStyle: 'italic', padding: '8px 0' }}>
                                        No accents saved yet. Select nodes and use ✂ Save Accent in the toolbar.
                                    </div>
                                )}
                                {accents.map((accent) => {
                                    // Compute bounding box for SVG viewBox
                                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                                    for (const cmd of accent.path) {
                                        if (cmd.x != null) { minX = Math.min(minX, cmd.x); minY = Math.min(minY, cmd.y); maxX = Math.max(maxX, cmd.x); maxY = Math.max(maxY, cmd.y); }
                                        if (cmd.x1 != null) { minX = Math.min(minX, cmd.x1); minY = Math.min(minY, cmd.y1); maxX = Math.max(maxX, cmd.x1); maxY = Math.max(maxY, cmd.y1); }
                                        if (cmd.x2 != null) { minX = Math.min(minX, cmd.x2); minY = Math.min(minY, cmd.y2); maxX = Math.max(maxX, cmd.x2); maxY = Math.max(maxY, cmd.y2); }
                                    }
                                    const w = maxX - minX || 1, h = maxY - minY || 1;
                                    const pad = Math.max(w, h) * 0.15;
                                    const d = buildRoundedPath(accent.path).map(cmd => {
                                        switch (cmd.type) {
                                            case 'M': return `M ${cmd.x} ${cmd.y}`;
                                            case 'L': return `L ${cmd.x} ${cmd.y}`;
                                            case 'C': return `C ${cmd.x1} ${cmd.y1} ${cmd.x2} ${cmd.y2} ${cmd.x} ${cmd.y}`;
                                            case 'Q': return `Q ${cmd.x1} ${cmd.y1} ${cmd.x} ${cmd.y}`;
                                            case 'Z': return 'Z';
                                            default: return '';
                                        }
                                    }).join(' ');

                                    return (
                                        <div key={accent.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            background: '#21262d', borderRadius: 6, padding: '6px 8px',
                                            border: '1px solid #30363d', cursor: 'pointer',
                                        }}>
                                            {/* Mini SVG preview */}
                                            <svg width="36" height="36" viewBox={`${minX - pad} ${minY - pad} ${w + pad * 2} ${h + pad * 2}`}
                                                style={{ background: '#0d1117', borderRadius: 4, flexShrink: 0 }}
                                                onClick={() => {
                                                    // Insert accent into glyph centered at advance width / 2
                                                    const offsetX = Math.round((glyph.advanceWidth || 600) / 2 - w / 2);
                                                    const offsetY = Math.round((metadata.xHeight || 520) / 2);
                                                    const { path: newPath, startIdx } = insertAccentPath(glyph.path, accent.path, offsetX, offsetY);
                                                    onGlyphChange({ ...glyph, path: newPath });
                                                    setSelectedIdx(startIdx);
                                                    setTool(TOOLS.SELECT);
                                                }}
                                            >
                                                <path d={d} fill="#da8ee744" stroke="#da8ee7" strokeWidth={Math.max(w, h) * 0.03} />
                                            </svg>

                                            {/* Name + insert */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 11, color: '#e6edf3', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {accent.name}
                                                </div>
                                                <div style={{ fontSize: 9, color: '#484f58' }}>
                                                    {accent.path.filter(c => c.type !== 'Z').length} pts
                                                </div>
                                            </div>

                                            {/* Insert button */}
                                            <Tooltip title="Insert into glyph">
                                                <Button type="text" size="small" icon={<PlusOutlined />}
                                                    onClick={() => {
                                                        const offsetX = Math.round((glyph.advanceWidth || 600) / 2 - w / 2);
                                                        const offsetY = Math.round((metadata.xHeight || 520) / 2);
                                                        const { path: newPath, startIdx } = insertAccentPath(glyph.path, accent.path, offsetX, offsetY);
                                                        onGlyphChange({ ...glyph, path: newPath });
                                                        setSelectedIdx(startIdx);
                                                        setTool(TOOLS.SELECT);
                                                    }}
                                                    style={{ color: '#3fb950', fontSize: 12 }} />
                                            </Tooltip>

                                            {/* Delete */}
                                            <Popconfirm
                                                title="Delete this accent?"
                                                onConfirm={() => {
                                                    onAccentsChange(accents.filter(a => a.id !== accent.id));
                                                }}
                                                okText="Delete"
                                                cancelText="Cancel"
                                                okButtonProps={{ danger: true }}
                                            >
                                                <Button type="text" size="small" icon={<DeleteOutlined />}
                                                    style={{ color: '#484f58', fontSize: 11 }} />
                                            </Popconfirm>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GlyphBezierEditor;
